<template>
  <li class="list-item talent-list-item">
    <div class="talent-list-item__container">
      <div class="talent-list-item__image" v-if="user.profile_image_url">
        <img :src="user.profile_image_url" />
      </div>
      <div class="talent-list-item__copy">
        <h4 class="truncate h4">
          {{ user.name }}
          <i v-if="user.profile && user.profile.visibility === 'hidden' && ($userIsAdmin || $userIsManager)" class="fa fa-eye-slash text-grey" aria-label="hidden" title="hidden"/>
          <i v-if="user.profile && user.profile.visibility === 'private' && ($userIsAdmin || $userIsManager)" class="fa fa-lock text-grey" aria-label="private" title="private"/>
        </h4>
        <div class="w-full">
          <div v-if="($userIsManager || $userIsAdmin) && bookingCancelledForUser" class="label label-danger">
            Cancelled
          </div>
          <div v-else-if="this.user.response && (this.user.response.budget || this.user.response.budget_per_star_as_user_rate)" class="label label-success">
            {{ this.user.response.budget ? this.user.response.budget : this.user.response.budget_per_star_as_user_rate | currency(booking.currency) }}
          </div>
        </div>
        <div class="w-full talent-list-item__buttons">
          <router-link v-if="showViewBio" tag="a" :to="{ name: 'talent.profile.view', params: { id: user.id, status: 'event' }}" target="_blank" class="btn btn-primary-outline btn-sm">
            <i class="fa fa-user" aria-hidden="true"></i> View Bio
          </router-link>

          <button v-if="photoIsPresent" class="btn btn-primary-outline btn-sm" @click="showLightBox">
            <lightbox
              :images="images"
              :showLightBox="showBox"
              :showCaption="true"
              :key="lightBoxKey">
            </lightbox>
            <i class="fa fa-camera" aria-hidden="true"></i> Promo Photos
          </button>

          <button v-else-if="!$userIsClient" disabled class="btn btn-primary-outline btn-padding btn-sm">
            <i class="fa fa-camera" aria-hidden="true"></i> No Photos
          </button>

          <div v-if="$paymentsModuleEnabled && booking.status === 'completed' && $userIsAdmin">
            <button v-if="this.user.response && this.user.response.talent_paid_notif_sent" disabled class="btn btn-primary-outline btn-padding btn-sm">
              <i class="fa fa-envelope"></i> Notify Payment Sent
            </button>
            <ladda-button
              v-else
              :click="handleNotifyTalentPaymentSentCallback"
              class="btn btn-primary pull-right btn-padding btn-sm">
              Send Notify Payment <i class="fa fa-send"></i>
            </ladda-button>
          </div>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { publicUrl, notify } from 'utils'
import Vue from 'vue'
import VueLazyLoad from 'vue-lazyload'
import Lightbox from 'vue-image-lightbox'
Vue.use(VueLazyLoad)

export default {
  components: {
    Lightbox
  },

  data () {
    return {
      images: [],
      showBox: false,
      lightBoxKey: 0
    }
  },

  props: {
    user: {
      required: true,
      type: Object
    },
    booking: {
      required: false,
      type: Object
    }
  },

  computed: {
    ...mapState('clientConfig', ['client_config']),

    /**
     * Determine whether talent has a profile image or at least one marketing photo
     *
     * @return bool
     */
    photoIsPresent () {
      return (this.user.photos_count && (this.user.photos_count !== 0))
    },

    bookingCancelledForUser () {
      return this.user && (this.user.response.status === 'cancelled')
    },

    profileUrl () {
      return this.$vlastMode ? '' : publicUrl(`our-stars/${this.user.id}/${this.user.slug}`)
    },

    showViewBio () {
      return !this.$vlastMode
    }
  },

  mounted () {
    if (this.photoIsPresent) {
      this.getTalentMarketingPhotos(this.user.id).then((response) => {
        response.forEach(photo => {
          // Lightbox component requires the images to be in this format
          this.images.push({
            thumb: photo.file_url,
            src: photo.file_url,
            caption: `<a @click="blur()" href="${photo.file_url}" class="btn">Download</a>`
          })
        })
      })
    }
  },

  methods: {
    ...mapActions('booking', ['notifyTalentPaymentSent']),
    ...mapActions('talent/profile', ['getTalentMarketingPhotos']),

    handleNotifyTalentPaymentSentCallback () {
      return this.notifyTalentPaymentSent({bookingId: this.booking.id, talentId: this.user.id})
        .then(() => {
          notify.success(`Notified ${this.user.name}`)
        }).catch(() => {
          notify.error(`Error occured while notifying ${this.user.name}. Please try again.`)
        })
    },

    /**
     * Since lightbox is opened by clicking the 'Promos Photos' button,
     * the component needs to be re-rendered so that the showLightBox
     * property is set to true. To achieve this, a key attached to the
     * component is incremented.
     */
    showLightBox (event) {
      this.showBox = true
      this.lightBoxKey += 1
    }
  }
}
</script>

<style lang="scss" scoped>
.talent-list-item {
  &__container {
    display: flex;
    align-items: center;
    @media (max-width: 574px) {
      flex-wrap: wrap;
    }
  }
  &__image {
    @media (max-width: 767px) {
      width: 60px;
      height: 60px;
    }
    width: 90px;
    height: 90px;
    position: relative;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    img {
      position: absolute;
      width: 100%;
      height: auto;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  &__copy {
    flex-grow: 1;
    @media (min-width: 575px) {
      padding-left: 30px;
    }
    h4 {
      font-size: 15px;
      font-family: "Open Sans", Sans-serif;
      text-transform: uppercase;
      font-weight: 700;
      letter-spacing: 2px;
      margin-bottom: 1em;
    }
  }
  &__buttons {
    .btn-sm {
      padding: 8px 16px !important;
    }
  }
}
</style>
